/***************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2020 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ***************************************************************************/
import { BaseHost } from '@ccx-comments/wrapper';
import IsomorphicMessageBridge from '../isomorphicMessageBridge';

/**
 * Host class with overrides for Internal UXP Webview
 */
class InternalHost extends BaseHost {
  /**
   * Constructor
   * @param {Object} initialState - initial state for component
   */
  constructor(initialState = {}, ...args) {
    const state = {
      pollForUpdates: false,
      reviewType: 'cloudDoc',
      ...initialState
    };

    super(state, ...args);

    /* HANDLE HOST EVENTS */
    IsomorphicMessageBridge.subscribe('batchCommentUpdate', (msgGuid, batchUpdatePayload) =>
      this.batchCommentUpdate(batchUpdatePayload)
    );
    IsomorphicMessageBridge.subscribe('createPendingAnnotation', (msgGuid, annotationPayload) =>
      this.createPendingAnnotation(annotationPayload)
    );
    IsomorphicMessageBridge.subscribe('fetchComments', () => this.providerRef.current.enableComments());
    IsomorphicMessageBridge.subscribe('highlightComment', (msgGuid, commentId) => this.highlightComment(commentId));
    IsomorphicMessageBridge.subscribe('reconfigureShareStatus', () => this.reconfigureShareStatus());
    IsomorphicMessageBridge.subscribe('remapComments', (msgGuid, remapObject) => this.remapComments(remapObject));
    IsomorphicMessageBridge.subscribe('removePendingAnnotation', () => this.removePendingAnnotation());
    IsomorphicMessageBridge.subscribe('setAssetMetadata', (msgGuid, assetData) => this.setAssetMetadata(assetData));
    IsomorphicMessageBridge.subscribe('setConfiguration', (msgGuid, config) => this.setConfiguration(config));
    IsomorphicMessageBridge.subscribe('setIMS', (msgGuid, imsData) => this.setIMS(imsData));
    IsomorphicMessageBridge.subscribe('setTheme', (msgGuid, theme) => this.setTheme(theme));
    IsomorphicMessageBridge.subscribe('showComment', (msgGuid, commentId) => this.showComment(commentId));
    IsomorphicMessageBridge.subscribe('unhighlightComment', () => this.unhighlightComment());
  }

  /* FETCHERS */

  /* eslint-disable class-methods-use-this*/
  /**
   * Returns promise for fetching asset metadata
   * @returns {Promise} assetMetadata promise
   */
  fetchAssetMetadata() {
    return new Promise(resolve => {
      IsomorphicMessageBridge.send('getAssetMetadata').then(assetData => resolve(assetData));
    });
  }

  /**
   * Returns promise for fetching configuration
   * @returns {Promise} configuration promise
   */
  fetchConfiguration() {
    return new Promise(resolve => {
      IsomorphicMessageBridge.send('getConfiguration').then(configuration => resolve(configuration));
    });
  }

  /**
   * Returns promise for fetching ims data
   * @returns {Promise} imsData promise
   */
  fetchImsData() {
    return new Promise(resolve => {
      IsomorphicMessageBridge.send('getIMSData').then(imsData => resolve(imsData));
    });
  }

  /**
   * Returns promise for fetching theme
   * @returns {Promise} theme promise
   */
  fetchTheme() {
    return new Promise(resolve => {
      IsomorphicMessageBridge.send('getTheme').then(theme => resolve(theme));
    });
  }

  /**
   * Method to handle communication of showComment response success
   * @param {Object} payload - payload object for showComment
   * @returns {undefined}
   */
  handleShowCommentResponse(payload) {
    IsomorphicMessageBridge.send('onShowCommentComplete', payload, undefined, false);
  }

  /* METHODS TO HANDLE COMPONENT EVENTS */

  /**
   * Callback for when annotation visbility is toggled.
   * @param {Boolean} visibility - visbility of annotations.
   * @param {Object} supplementaryData - Other data important to understanding the event
   * @returns {undefined}
   */
  onAnnotationToggle(visibility, supplementaryData) {
    IsomorphicMessageBridge.send(
      'onAnnotationToggle',
      {
        visibility,
        supplementaryData
      },
      undefined,
      false
    );
  }

  /**
   * Callback for when annotating mode changes
   * @param {Object} payload - object containing annotating change data
   * @param {Object} supplementaryData - Other data important to understanding the event
   * @returns {undefined}
   */
  onAnnotatingChange(payload, supplementaryData) {
    IsomorphicMessageBridge.send(
      'onAnnotatingChange',
      {
        payload,
        supplementaryData
      },
      undefined,
      false
    );
  }

  /**
   * Callback for when a comment is clicked.
   * @param {Object} commentId - id for clicked comment.
   * @param {Object} supplementaryData - Other data important to understanding the event
   * @returns {undefined}
   */
  onCommentClick(commentId, supplementaryData) {
    IsomorphicMessageBridge.send(
      'onCommentClick',
      {
        commentId,
        supplementaryData
      },
      undefined,
      false
    );
  }

  /**
   * Callback for when a comment is hovered.
   * @param {String} commentId - id for hovered comment.
   * @param {Object} supplementaryData - Other data important to understanding the event
   * @returns {undefined}
   */
  onCommentHover(commentId, supplementaryData) {
    const { isHovered, hasAnnotation } = supplementaryData;

    if (hasAnnotation) {
      const method = isHovered ? 'onCommentHover' : 'onCommentUnhover';

      IsomorphicMessageBridge.send(
        method,
        {
          commentId,
          supplementaryData
        },
        undefined,
        false
      );
    }
  }

  /**
   * Callback for comment loaded
   * @param {Object} supplementaryData - Other data important to understanding the event
   * @returns {undefined}
   */
  onCommentsSuccess(supplementaryData) {
    IsomorphicMessageBridge.send('onCommentsSuccess', { supplementaryData }, undefined, false);
  }

  /**
   * Callback for comment updates from the component.
   * @param {Object} commentCounts - Various comment count totals.
   * @param {Object} delta - Comment delta detailing what changed.
   * @param {Object} supplementaryData - Other data important to understanding the event
   * @returns {undefined}
   */
  onCommentsUpdate(commentCounts, delta, supplementaryData) {
    IsomorphicMessageBridge.send('onCommentsUpdate', { commentCounts, ...delta, supplementaryData }, undefined, false);
  }

  /**
   * Callback for when active node changes.
   * @param {Boolean} nodeId - id of node being being activated.
   * @param {String} sectionId - id of section being being activated.
   * @param {Object} supplementaryData - Other data important to understanding the event
   * @returns {undefined}
   */
  onNodeChange(nodeId, sectionId, supplementaryData) {
    IsomorphicMessageBridge.send(
      'onNodeChange',
      {
        nodeId,
        sectionId,
        supplementaryData
      },
      undefined,
      false
    );
  }

  /**
   * Callback to notify host sharing is being requested.
   * @param {String} reviewType - type of asset review.
   * @param {Object} supplementaryData - Other data important to understanding the event
   * @returns {undefined}
   */
  onShare(reviewType, supplementaryData) {
    IsomorphicMessageBridge.send(
      'onShare',
      {
        reviewType,
        supplementaryData
      },
      undefined,
      false
    );
  }
  /* eslint-enable class-methods-use-this*/
}

export default InternalHost;
